import { useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMayWrite } from "../cellar.js";
import { actionButton, control, resting } from "./controls.js";

/**
 * What a cabinet, or one shelf in it, is set to.
 *
 * It earns a field of its own because of what it decides: the reaction rate
 * roughly doubles per 8–10 °C, so a window of 12–25 years at 12° is
 * realistically 6–13 at 20°. Of everything that moves a drinking window this is
 * the largest, and it is the only one no outside source can ever supply — it is
 * a fact about this cellar and nobody else's.
 *
 * Empty is *not known* and is saved as such, which is the honest state for a
 * cabinet with no thermometer in it. On a shelf, empty means **whatever the
 * cabinet says** — the two are not the same statement, and this is the reason
 * a shelf may leave it blank without claiming to agree.
 *
 * The button appears only when the number has actually changed, the same rule
 * `OwnRating` follows: a save that saves what is already stored is a request
 * nobody asked for, and a control that is always live invites a tap that does
 * nothing.
 */
export function Temperature({
  label,
  value,
  onSave,
}: {
  label: string;
  value: number | null;
  onSave: (next: number | null) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const fieldId = useId();
  const stored = value === null ? "" : String(value);
  const [text, setText] = useState(stored);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const mayWrite = useMayWrite();

  // A guest reads the temperature and does not set it. The number itself is
  // worth showing them — it is the largest single thing moving the drinking
  // windows they are looking at — so this becomes a word rather than
  // disappearing, and a cabinet with no thermometer says so.
  if (mayWrite === false) {
    // A number is the fact and is set in the display face; *not known* is the
    // absence of one and is quiet, so a cabinet with no thermometer does not
    // announce itself as loudly as one that has been measured.
    return value === null ? (
      <span className="text-muted text-sm">{t("cellar.temperatureUnknown")}</span>
    ) : (
      <span className="font-display text-ink text-sm">{value} °C</span>
    );
  }

  const given = text.trim() !== "";
  const degrees = Number(text);
  // The same bounds the contract enforces, checked here so a typo is refused
  // before it becomes a request: nothing anybody stores wine in is below
  // freezing or above thirty, and a mistyped 120 would otherwise claim that
  // every bottle in the cabinet is cooked.
  const sound =
    !given && text === ""
      ? true
      : Number.isInteger(degrees) && degrees >= 0 && degrees <= 30;
  const changed = text.trim() !== stored;

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        if (busy || !sound || !changed) return;
        setBusy(true);
        setError(null);
        void onSave(given ? degrees : null)
          .catch(() => setError(t("cellar.temperatureFailed")))
          .finally(() => setBusy(false));
      }}
    >
      <label className="sr-only" htmlFor={fieldId}>
        {label}
      </label>
      {/* The width sits on the wrapper: `control` carries `w-full`, and two
          width utilities on one element are decided by the stylesheet's order
          rather than by this file. */}
      <div className="w-16">
        <input
          id={fieldId}
          value={text}
          inputMode="numeric"
          onChange={(event) => setText(event.target.value)}
          placeholder="°C"
          aria-invalid={!sound}
          className={`${control} ${resting} text-right`}
        />
      </div>
      {changed && (
        <button type="submit" disabled={busy || !sound} className={actionButton}>
          {t("cellar.saveTemperature")}
        </button>
      )}
      {error !== null && (
        <span role="alert" className="text-past text-sm">
          {error}
        </span>
      )}
    </form>
  );
}
